import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Col, Form, Input, Layout, message, Row } from 'antd';
import { get } from 'lodash';

import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';

import ResellerServices from 'services/reseller';
import { errHandling } from 'utils';

const { Content } = Layout;
const { TextArea } = Input;

const TITLE = {
  BANNER: 'Banner',
  BENEFIT: 'Benefit',
  STEPS: 'Steps',
};

const SIZE = {
  BANNER: 'Mobile Image (640x360 px)',
  BENEFIT: 'Image (300x300 px)',
  STEPS: 'Image (100x100 px)',
};

const SCREEN = {
  DESKTOP: 'desktop_media_url',
  MOBILE: 'mobile_media_url',
};

export function ResellerPageCreateEdit() {
  const [form] = Form.useForm();
  const [loadingButton, setLoadingButton] = useState(false);
  const [media, setMedia] = useState({});

  const navigate = useNavigate();
  const location = useLocation();
  const { detail = {}, page = '' } = get(location, 'state', {}) || {};
  const {
    benefit_title = '',
    benefit_title_idn = '',
    description = '',
    description_idn = '',
    id = '',
    procedure_title = '',
    procedure_title_idn = '',
    title = '',
    title_idn = '',
  } = detail;

  async function onFinish(values) {
    if (!id) {
      setLoadingButton(true);
      let length = page === 'BENEFIT' ? 'benefits.length' : 'procedures.length';
      let order_level = `${get(detail, length, 0) + 1}`;

      try {
        if (page === 'BANNER') {
          await ResellerServices.createPageDetail({ ...values, ...media });
        } else if (page === 'BENEFIT') {
          await ResellerServices.createPageDetail({
            business_benefits_attributes: [
              { ...values, ...media, order_level },
            ],
          });
        } else if (page === 'STEPS') {
          await ResellerServices.createPageDetail({
            business_procedures_attributes: [
              { ...values, ...media, order_level },
            ],
          });
        }

        navigate('/resellers_page');
        message.success(`New ${TITLE[page]} has been added`);
      } catch (error) {
        errHandling(error);
      } finally {
        setLoadingButton(false);
      }
    } else if (id) {
      setLoadingButton(true);
      try {
        if (page === 'BENEFIT')
          await ResellerServices.createPageDetail({
            business_benefits_attributes: [{ id, ...values, ...media }],
          });
        else if (page === 'STEPS')
          await ResellerServices.createPageDetail({
            business_procedures_attributes: [{ id, ...values, ...media }],
          });

        navigate('/resellers_page');
        message.success(`${TITLE[page]} has been edited`);
      } catch (error) {
        errHandling(error);
      }
    }
  }

  function handleChangeMedia(url, key) {
    setMedia((prevState) => ({
      ...prevState,
      [key]: url,
    }));
  }

  useEffect(() => {
    if (detail && page === 'BANNER')
      setMedia({
        [SCREEN.DESKTOP]: detail[SCREEN.DESKTOP],
        [SCREEN.MOBILE]: detail[SCREEN.MOBILE],
      });
    else if (id)
      setMedia({
        [SCREEN.DESKTOP]: detail[SCREEN.DESKTOP],
        [SCREEN.MOBILE]: detail[SCREEN.MOBILE],
      });
    // eslint-disable-next-line
  }, []);

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">
          {`${id ? 'Edit' : 'Create New'} ${TITLE[page]}`}
        </div>
        <Content className="pageContent">
          <div className="section-container">
            <div className="inner-section">
              <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
                initialValues={{
                  title: id || page === 'BANNER' ? title : '',
                  title_idn: id || page === 'BANNER' ? title_idn : '',
                  description: id || page === 'BANNER' ? description : '',
                  description_idn:
                    id || page === 'BANNER' ? description_idn : '',
                  benefit_title: id || page === 'BANNER' ? benefit_title : '',
                  benefit_title_idn:
                    id || page === 'BANNER' ? benefit_title_idn : '',
                  procedure_title:
                    id || page === 'BANNER' ? procedure_title : '',
                  procedure_title_idn:
                    id || page === 'BANNER' ? procedure_title_idn : '',
                  [SCREEN.DESKTOP]: get(detail, SCREEN.DESKTOP, ''),
                  [SCREEN.MOBILE]:
                    id || page === 'BANNER'
                      ? get(detail, SCREEN.MOBILE, '')
                      : '',
                }}>
                {page !== 'BENEFIT' && (
                  <Row justify="space-around" type="flex">
                    <Col span={11}>
                      <Form.Item
                        label={`${TITLE[page]} Title`}
                        name="title"
                        rules={[
                          {
                            message: 'Please input title',
                            required: page !== 'BENEFIT',
                          },
                        ]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item
                        label={`${TITLE[page]} Title (Bahasa)`}
                        name="title_idn"
                        rules={[
                          {
                            message: 'Please input title',
                            required: true,
                          },
                        ]}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
                <Row type="flex" justify="space-around">
                  <Col span={11}>
                    <Form.Item
                      label={`${TITLE[page]} Description`}
                      name="description"
                      rules={[
                        {
                          message: 'Please input description',
                          required: true,
                        },
                      ]}>
                      <TextArea rows={5} />
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item
                      label={`${TITLE[page]} Description (Bahasa)`}
                      name="description_idn"
                      rules={[
                        {
                          message: 'Please input description',
                          required: true,
                        },
                      ]}>
                      <TextArea rows={5} />
                    </Form.Item>
                  </Col>
                </Row>

                {page === 'BANNER' && (
                  <>
                    <Row type="flex" justify="space-around">
                      <Col span={11}>
                        <Form.Item
                          label={`First Content Title`}
                          name="benefit_title"
                          rules={[
                            {
                              message: 'Please input content title',
                              required: page !== 'BENEFIT',
                            },
                          ]}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={11}>
                        <Form.Item
                          label={`First Content Title (Bahasa)`}
                          name="benefit_title_idn"
                          rules={[
                            {
                              message: 'Please input content title',
                              required: page !== 'BENEFIT',
                            },
                          ]}>
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row type="flex" justify="space-around">
                      <Col span={11}>
                        <Form.Item
                          label={`Second Content Title`}
                          name="procedure_title"
                          rules={[
                            {
                              message: 'Please input content title',
                              required: page !== 'BENEFIT',
                            },
                          ]}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={11}>
                        <Form.Item
                          label={`Second Content Title (Bahasa)`}
                          name="procedure_title_idn"
                          rules={[
                            {
                              message: 'Please input content title',
                              required: page !== 'BENEFIT',
                            },
                          ]}>
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      label="Desktop Image (1440x810 px)"
                      name={SCREEN.DESKTOP}>
                      <PdUploader
                        afterUpload={handleChangeMedia}
                        imgClassName="w-full"
                        imgUrl={media[SCREEN.DESKTOP]}
                        keyValue={SCREEN.DESKTOP}
                      />
                    </Form.Item>
                  </>
                )}
                <Form.Item label={SIZE[page]} name={SCREEN.MOBILE}>
                  <PdUploader
                    afterUpload={handleChangeMedia}
                    imgClassName="w-40"
                    imgUrl={media[SCREEN.MOBILE]}
                    keyValue={SCREEN.MOBILE}
                  />
                </Form.Item>
                <Form.Item>
                  <PdButton
                    full
                    htmlType="submit"
                    loading={loadingButton}
                    type="primary">
                    Publish
                  </PdButton>
                </Form.Item>
              </Form>
            </div>
          </div>
        </Content>
      </div>
    </Layout>
  );
}

export default ResellerPageCreateEdit;
